import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { inicioDoAviso, prazoDoPerfil, somarDias, type PrazoDoLaboratorio } from "@bookdental/schemas";
import { Input, Label, Switch } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";

const inteiro = (v: string, min: number, max: number) => {
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
};

/**
 * Prazo de produção do laboratório: quantos dias (úteis ou corridos), se o sábado conta e quantos
 * dias antes do prazo avisar a produção. Passa do prazo sem data combinada com a clínica, o trabalho
 * entra em "Atrasados". Só aparece em organização laboratório.
 */
export function PrazoDeProducaoSection({ canManage }: { canManage: boolean }) {
  const utils = trpc.useUtils();
  const perfil = trpc.organization.getProfiles.useQuery();
  const ehLab = (perfil.data?.types ?? []).includes("DENTAL_LAB");
  const salvo = prazoDoPerfil(perfil.data?.labProfile);
  const [dias, setDias] = useState(String(salvo.dias));
  const [diasUteis, setDiasUteis] = useState(salvo.diasUteis);
  const [sabado, setSabado] = useState(salvo.sabado);
  const [avisar, setAvisar] = useState(salvo.avisoAntes != null);
  const [antes, setAntes] = useState(String(salvo.avisoAntes ?? 2));
  const chaveSalva = JSON.stringify(salvo);
  useEffect(() => {
    setDias(String(salvo.dias));
    setDiasUteis(salvo.diasUteis);
    setSabado(salvo.sabado);
    setAvisar(salvo.avisoAntes != null);
    setAntes(String(salvo.avisoAntes ?? 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveSalva]);

  const salvar = trpc.organization.setPrazoDeProducao.useMutation({
    onSuccess: () => {
      utils.organization.getProfiles.invalidate();
      utils.organization.getOrgPreview.invalidate();
      utils.prosthesis.incoming.list.invalidate();
      utils.prosthesis.lab.options.invalidate();
      toast.success("Prazo de produção atualizado.");
    },
    onError: (e) => toast.error(e.message || "Não foi possível salvar o prazo."),
  });

  if (!ehLab) return null;
  const nDias = inteiro(dias, 1, 90);
  const nAntes = avisar ? inteiro(antes, 1, 30) : null;
  const valido = nDias != null && (!avisar || nAntes != null);
  const novo: PrazoDoLaboratorio | null = valido
    ? { dias: nDias, diasUteis, sabado: diasUteis && sabado, avisoAntes: nAntes }
    : null;
  const mudou = novo != null && JSON.stringify(novo) !== chaveSalva;
  const unidade = diasUteis ? "úteis" : "corridos";

  const vence = novo ? somarDias(new Date(), novo.dias, novo) : null;
  const aviso = novo && vence ? inicioDoAviso(vence, novo) : null;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="prazo-producao">Prazo de produção</Label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            id="prazo-producao"
            type="number"
            inputMode="numeric"
            min={1}
            max={90}
            value={dias}
            onChange={(e) => setDias(e.target.value)}
            disabled={!canManage}
            className="w-20"
          />
          <div role="radiogroup" aria-label="Contagem" className="inline-flex rounded-md border border-zinc-200 p-0.5 dark:border-border">
            {[
              { valor: true, rotulo: "Dias úteis" },
              { valor: false, rotulo: "Dias corridos" },
            ].map((o) => (
              <button
                key={o.rotulo}
                type="button"
                role="radio"
                aria-checked={diasUteis === o.valor}
                disabled={!canManage}
                onClick={() => setDiasUteis(o.valor)}
                className={cn(
                  "rounded px-3 py-1.5 text-[12.5px] font-medium transition-colors disabled:opacity-60",
                  diasUteis === o.valor
                    ? "bg-brand-50 text-brand-700 dark:bg-primary/15 dark:text-primary"
                    : "text-zinc-600 hover:bg-zinc-50 dark:text-muted-foreground dark:hover:bg-muted",
                )}
              >
                {o.rotulo}
              </button>
            ))}
          </div>
        </div>
        <p className="text-[11.5px] text-zinc-500 dark:text-muted-foreground">
          {diasUteis
            ? "Domingos e feriados nacionais não contam. Trabalho sem prazo combinado com a clínica entra em \"Atrasados\" depois desses dias."
            : "Trabalho sem prazo combinado com a clínica entra em \"Atrasados\" depois desses dias."}
        </p>
      </div>

      {diasUteis && (
        <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-foreground/90">
          <Switch checked={sabado} onCheckedChange={setSabado} disabled={!canManage} />
          Trabalhamos aos sábados
        </label>
      )}

      <div className="space-y-1.5">
        <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-foreground/90">
          <Switch checked={avisar} onCheckedChange={setAvisar} disabled={!canManage} />
          Avisar a produção antes do prazo
        </label>
        {avisar && (
          <div className="flex items-center gap-2 pl-[46px] text-sm text-zinc-700 dark:text-foreground/90">
            <Input
              aria-label="Dias antes do prazo"
              type="number"
              inputMode="numeric"
              min={1}
              max={30}
              value={antes}
              onChange={(e) => setAntes(e.target.value)}
              disabled={!canManage}
              className="w-16"
            />
            dias {unidade} antes
          </div>
        )}
        <p className="text-[11.5px] text-zinc-500 dark:text-muted-foreground">
          No dia do aviso, quem cuida da produção recebe uma notificação e o pedido ganha o selo "Vence em breve".
        </p>
      </div>

      {vence && (
        <p className="rounded-md bg-zinc-50 px-3 py-2 text-[12px] text-zinc-600 dark:bg-muted/50 dark:text-muted-foreground">
          Exemplo: um pedido de hoje, sem prazo combinado, vence em <strong>{format(vence, "dd/MM")}</strong>
          {aviso ? (
            <>
              {" "}e o aviso chega em <strong>{format(aviso, "dd/MM")}</strong>.
            </>
          ) : (
            "."
          )}
        </p>
      )}

      {canManage && (
        <button
          type="button"
          onClick={() =>
            novo &&
            salvar.mutate({
              slaDays: novo.dias,
              prazoEmDiasUteis: novo.diasUteis,
              trabalhaSabado: novo.sabado,
              avisoDeUrgencia: novo.avisoAntes,
            })
          }
          disabled={salvar.isPending || !mudou}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
        >
          {salvar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Salvar prazo
        </button>
      )}
    </div>
  );
}
